<template>
  <v-app v-if="applicationIsAuth">
    <toolbar v-model:sidebar-shown="sidebarShown" :sidebar-is-shown="sidebarIsShown" />
    <sidebar v-if="sidebarIsShown" v-model:sidebar-shown="sidebarShown" />
    <v-main class="bg-secondary-100">
      <ad-block-alert v-if="isAdBlockDetected && isBeta" :text="$t('main.adblock_detected')" />
      <v-container fluid class="padding-for-live-chat bg-secondary-100">
        <router-view />
      </v-container>
      <layout-modals v-if="modalLayoutIsShown" />
      <c-card
        v-if="draftIsShown && isBeta"
        :to="{ name: routeNames.CREATE_CAMPAIGN }"
        height="48px"
        class="d-flex align-center black--text font-weight-bold text-subtitle-1 draft-button border-secondary px-4"
      >
        <v-icon size="20" color="primary" class="mr-3"> $rocket </v-icon>
        {{ $t('main.campaign_draft.continue_campaign') }}
      </c-card>
    </v-main>
  </v-app>
  <loader v-else />
</template>

<script>
  import { mapState, mapActions } from 'pinia'
  import * as Sentry from '@sentry/vue'
  import Sidebar from '@/components/main/Sidebar/Index.vue'
  import Toolbar from '@/components/main/Toolbar.vue'
  import Loader from '@/components/Loader.vue'
  import setGlobalUserId from '@/mixins/setGlobalUserId'
  import saveUserToCookie from '@/mixins/saveUserToCookie'
  import LayoutModals from '@/components/LayoutModals.vue'
  import AdBlockAlert from '@/components/AdBlockAlert.vue'
  import { useSettingsStore } from '@/store/settings/settings'
  import { usePersonalStore } from '@/store/personal/personal'
  import routeNames from '@/types/route-names'
  import guardSections from '@/types/guard-sections'
  import { draftsRepository } from '@/services/repository-factory'
  import handleErrors from '@/services/handleErrors'
  import { CCard } from '@clickadilla/components/components'

  export default {
    name: 'MainLayouts',
    components: {
      AdBlockAlert,
      LayoutModals,
      Sidebar,
      Toolbar,
      Loader,
      CCard
    },
    mixins: [setGlobalUserId, saveUserToCookie],
    props: {
      isAdBlockDetected: {
        type: Boolean,
        required: true
      }
    },
    data() {
      return {
        modalLayoutIsShown: false,
        sidebarShown: true,
        draft: null,
        routeNames
      }
    },
    computed: {
      ...mapState(useSettingsStore, ['applicationIsAuth', 'adNetwork', 'sidebarIsShown']),
      ...mapState(usePersonalStore, ['fetchIsLoading']),
      user() {
        return this.$auth.user ?? {}
      },
      isBeta() {
        return window.localStorage.getItem('isBeta')
      },
      adNetworkHasSectionCampaigns() {
        return this.adNetwork.guardSections.some(({ name }) => name === guardSections.CAMPAIGNS)
      },
      draftIsShown() {
        const routeIsAvailable = ![
          routeNames.CREATE_CAMPAIGN,
          routeNames.EDIT_CAMPAIGN,
          routeNames.DUPLICATE_CAMPAIGN,
          routeNames.OFFER_CAMPAIGN
        ].includes(this.$route.name)
        return this.adNetworkHasSectionCampaigns && !!this.draft && routeIsAvailable
      }
    },
    watch: {
      fetchIsLoading: {
        handler(value) {
          if (value === false) {
            this.modalLayoutIsShown = true
          }
        }
      }
    },
    async created() {
      this.sidebarShown = !this.$vuetify.display.mobile
      this.setIsSidebarShown(this.sidebarShown)
      Sentry.setUser({
        id: String(this.user.id),
        username: this.user.name,
        email: this.user.email
      })
      this.initLiveChat()
      this.initDisplayPopup()
      if (this.adNetworkHasSectionCampaigns) {
        this.fetchDraft()
      }
    },
    beforeUnmount() {
      if (this.adNetwork.settings.liveChat.type === 'intercom') {
        this.$intercom.shutdown()
      }
    },
    mounted() {
      if (!this.$pixel.payload.user_id && this.user.id) {
        this.$pixel.setPayload({
          user_id: this.user.id,
          is_impersonating: !!this.user.is_impersonating,
          ...(!!this.user.is_impersonating && { impersonator_id: this.user.impersonator?.id })
        })
      }
    },
    methods: {
      ...mapActions(useSettingsStore, ['setIsSidebarShown']),
      async fetchDraft() {
        try {
          const draft = await draftsRepository.show()
          this.draft = Array.isArray(draft) ? (draft[0] ?? null) : draft
        } catch (error) {
          handleErrors(error)
        }
      },
      initLiveChat() {
        const { liveChat } = this.adNetwork.settings
        if (liveChat.type === 'intercom') {
          this.$intercom.init({
            app_id: liveChat.intercom.app_id,
            name: this.user.name,
            user_id: this.user.id,
            email: this.user.email,
            user_hash: this.user.intercom_hash
          })
        } else if (liveChat.type === 'tawk') {
          this.$tawk.init({
            widgetId: liveChat.tawk.widget_id,
            accountKey: liveChat.tawk.account_key,
            name: this.user.name,
            email: this.user.email
          })
        } else if (liveChat.type === 'dashly') {
          this.$dashly.init({
            apiKey: liveChat.dashly.api_key,
            userHash: this.user.dashly_hash,
            userId: this.user.id
          })
        } else if (liveChat.type === 'crisp') {
          this.$crisp.init({
            websiteId: this.adNetwork.crispWebsiteId,
            email: this.user.email,
            emailHash: this.user.crisp_hash
          })
        }
      },
      initDisplayPopup() {
        const { displayPopup } = this.adNetwork.settings
        if (displayPopup.type === 'getsitecontrol' && displayPopup?.getsitecontrol.gsc_id) {
          this.$gsc.init({
            gscId: displayPopup.getsitecontrol.gsc_id,
            name: this.user.name,
            email: this.user.email,
            userId: this.user.id
          })
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .padding-for-live-chat {
    padding-bottom: 90px;
  }
  .draft-button {
    position: fixed;
    right: 98px;
    bottom: 24px;
    z-index: 5;
    box-shadow: 0 3px 16px 0 rgba(0, 0, 0, 0.08) !important;
  }
</style>
